import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Select from "react-select";
import moment from "moment";
import FlatPickr from "react-flatpickr";
import PulseLoader from "react-spinners/PulseLoader";
import { ToastContainer, toast } from "react-toastify";
import { ColumnDef, ColumnFiltersState } from "@tanstack/react-table";
import {
  AlertCircle,
  BarChart3,
  Calendar,
  CheckCircle2,
  Clock,
  Columns3,
  Download,
  FileSpreadsheet,
  Filter,
  Mail,
  MapPin,
  Percent,
  Search,
  ShieldCheck,
  Tag,
  Ticket,
  X,
} from "lucide-react";
import { calculateSummaryStats } from "../../../utils/revenueCalculator";
import {
  downloadTicketByBookingNo,
  getAllBookingByDateRange,
  getAllTicketBySlug,
  getAllVenueBySlug,
  getReport,
  searchBookings,
  searchBookingsByContact,
} from "../../../api/index";
import PageHeader from "../../../components/common/PageHeader/PageHeader";
import TablesV2 from "../../../components/common/Tables/TablesV2";
import FormattedDate from "../../../components/common/DateFormat/FormattedDate";
import Background from "../../Login/Background";
import { useNightMode } from "../../../contexts/NightModeContext";
import { useAuth } from "../../../context/AuthContext";
import { getOrganizerCurrency } from "../../../utils/currency";

interface Booking {
  bookingId: string;
  organizerId: string;
  [key: string]: any;
}

const searchTypeOptions = [
  { value: "event", label: "Event", icon: Calendar },
  { value: "date", label: "Date Range", icon: Calendar },
  { value: "ticket", label: "Ticket Type", icon: Tag },
  { value: "venue", label: "Venue", icon: MapPin },
  { value: "email", label: "Email / Phone", icon: Mail },
];

const availableColumns = [
  { value: "bookingNo", label: "Booking No" },
  { value: "name", label: "Name" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "ticketType", label: "Ticket Type" },
  { value: "ticketCount", label: "Ticket Count" },
  { value: "ticketAmount", label: "Ticket Amount" },
  { value: "grandTotal", label: "Grand Total" },
  { value: "bookingStatus", label: "Booking Status" },
  { value: "eventName", label: "Event Name" },
  { value: "venueName", label: "Venue Name" },
  { value: "couponCode", label: "Coupon Code" },
  { value: "discountAmount", label: "Discount Amount" },
  { value: "createdAt", label: "Created Date" },
  { value: "action", label: "Action" },
];

const EventRunReport: React.FC = () => {
  const { isNightMode } = useNightMode();
  const { user } = useAuth();
  const { eventId: paramEventId } = useParams();
  const currencySymbol = getOrganizerCurrency().symbol;
  const organizerData = localStorage.getItem("organizerData");
  const parsedOrganizerData = organizerData ? JSON.parse(organizerData) : null;
  const eventData = localStorage.getItem("eventData");
  const parsedEventData = eventData ? JSON.parse(eventData) : null;
  
  const organizerId = parsedOrganizerData?.organizerId || user?.organizerId || parsedEventData?.Organizer?.organizerId || parsedEventData?.organizerId;
  const eventIdFromLocal = parsedEventData?.eventId;
  const targetEventId = eventIdFromLocal || paramEventId;
  const eventSlug = parsedEventData?.slugId || paramEventId;
  const hasMultipleVenues = Boolean(parsedEventData?.hasMultipleVenues);

  const [searchCriteria, setSearchCriteria] = useState({
    fromDate: null as Date | null,
    toDate: null as Date | null,
    selectedTicketType: null as any,
    selectedVenue: null as any,
    emailOrPhone: "",
    searchType: "event",
  });
  const [venueList, setVenueList] = useState<any[]>([]);
  const [ticketTypeList, setTicketTypeList] = useState<any[]>([]);
  const [bookingList, setBookingList] = useState<Booking[]>([]);
  const [filteredBookingList, setFilteredBookingList] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [downloadingTicketId, setDownloadingTicketId] = useState<string | null>(null);
  const [tableSearchQuery, setTableSearchQuery] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([
    "bookingNo",
    "name",
    "email",
    "phone",
    "ticketType",
    "ticketCount",
    "grandTotal",
    "bookingStatus",
    "action",
  ]);
  const [summaryStats, setSummaryStats] = useState<any>({});

  useEffect(() => {
    const loadEventRelatedData = async () => {
      if (!eventSlug) return;
      try {
        const [venuesResp, ticketsResp] = await Promise.all([
          getAllVenueBySlug(eventSlug),
          getAllTicketBySlug(eventSlug),
        ]);
        setVenueList((venuesResp || []).map((item: any) => ({ value: item.venueId, label: item.venueName })));
        setTicketTypeList((ticketsResp || []).map((item: any) => ({ value: item.ticketTypeId, label: item.ticketType, price: item.price })));
      } catch (err) {
        console.error("Failed to load ticket/venue data", err);
      }
    };

    loadEventRelatedData();
  }, [eventSlug]);

  const performBookingSearch = async () => {
    if (!targetEventId) return;
    setLoading(true);
    setError("");
    let bookings: Booking[] = [];

    const ensureArray = (res: any): Booking[] => {
      if (Array.isArray(res)) return res;
      if (res && Array.isArray(res.data)) return res.data;
      if (res && Array.isArray(res.bookings)) return res.bookings;
      return [];
    };

    try {
      switch (searchCriteria.searchType) {
        case "event": {
          const resp = await getReport(targetEventId);
          bookings = ensureArray(resp);
          break;
        }
        case "date": {
          if (searchCriteria.fromDate && searchCriteria.toDate) {
            const response = await getAllBookingByDateRange(searchCriteria.fromDate, searchCriteria.toDate);
            const arr = ensureArray(response);
            bookings = arr.filter((booking: Booking) => Number(booking.eventId) === Number(targetEventId));
          }
          break;
        }
        case "ticket": {
          if (searchCriteria.selectedTicketType) {
            const response = await getReport(targetEventId);
            const arr = ensureArray(response);
            bookings = arr.filter((booking: Booking) => Number(booking.ticketTypeId) === Number(searchCriteria.selectedTicketType.value));
          }
          break;
        }
        case "venue": {
          if (searchCriteria.selectedVenue) {
            const resp = await searchBookings({ eventId: targetEventId, venueId: searchCriteria.selectedVenue.value, organizerId });
            bookings = ensureArray(resp);
          }
          break;
        }
        case "email": {
          if (searchCriteria.emailOrPhone.trim()) {
            const response = await searchBookingsByContact(searchCriteria.emailOrPhone);
            const arr = ensureArray(response?.data || response);
            bookings = arr.filter((booking: Booking) => Number(booking.eventId) === Number(targetEventId));
          }
          break;
        }
        default:
          break;
      }

      let activeBookings = bookings.filter((booking: Booking) => booking.paymentStatus !== "expired");
      if (organizerId) {
        const filteredByOrg = activeBookings.filter((booking: Booking) => !booking.organizerId || Number(booking.organizerId) === Number(organizerId));
        if (filteredByOrg.length > 0) {
          activeBookings = filteredByOrg;
        }
      }

      setBookingList(activeBookings);
      setFilteredBookingList(activeBookings);
      setSummaryStats(calculateSummaryStats(activeBookings));
      setTableSearchQuery("");
      setColumnFilters([]);
    } catch (err) {
      console.error("Booking search error:", err);
      // Fallback gracefully to empty state if no bookings or network error
      setBookingList([]);
      setFilteredBookingList([]);
      setSummaryStats(calculateSummaryStats([]));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchCriteria.searchType === "event" && targetEventId) {
      performBookingSearch();
    }
  }, [searchCriteria.searchType, targetEventId, organizerId]);

  const isSearchValid = () => {
    switch (searchCriteria.searchType) {
      case "event":
        return Boolean(targetEventId);
      case "date":
        return Boolean(searchCriteria.fromDate && searchCriteria.toDate);
      case "ticket":
        return Boolean(searchCriteria.selectedTicketType);
      case "venue":
        return Boolean(searchCriteria.selectedVenue);
      case "email":
        return searchCriteria.emailOrPhone.trim().length > 0;
      default:
        return false;
    }
  };

  const handleSearchTypeChange = (type: string) => {
    setSearchCriteria({ fromDate: null, toDate: null, selectedTicketType: null, selectedVenue: null, emailOrPhone: "", searchType: type });
  };

  const handleClear = () => {
    setSearchCriteria({ fromDate: null, toDate: null, selectedTicketType: null, selectedVenue: null, emailOrPhone: "", searchType: "event" });
    setFilteredBookingList([]);
    setBookingList([]);
    setError("");
    setTableSearchQuery("");
    setColumnFilters([]);
  };

  const transformedData = useMemo(() => filteredBookingList.map((booking) => {
    const contactDetails = booking.bookingObject?.primaryContactDetails || {};
    return {
      id: booking.bookingId || booking.bookingNo,
      bookingId: booking.bookingId,
      bookingNo: booking.bookingNo || booking.primaryBookingNo || "N/A",
      name: `${contactDetails.firstName || ""} ${contactDetails.lastName || ""}`.trim() || "N/A",
      email: contactDetails.email || "N/A",
      phone: contactDetails.phoneNo || "N/A",
      ticketType: booking.TicketType?.ticketType || booking.ticketType || "N/A",
      ticketCount: Number(booking.totalTicketCount || 0),
      ticketAmount: Number(booking.totalTicketAmount || 0),
      grandTotal: Number(booking.grandTotal || 0),
      bookingStatus: booking.bookingStatus || "N/A",
      eventName: booking.Event?.eventName || parsedEventData?.eventName || "N/A",
      venueName: booking.Venue?.venueName || "N/A",
      couponCode: booking.couponCode || "N/A",
      discountAmount: Number(booking.discountAmount || 0),
      createdAt: booking.createdAt,
      originalBooking: booking,
    };
  }), [filteredBookingList, parsedEventData?.eventName]);

  const handleDownloadTicket = async (row: any) => {
    const bookingNo = row?.bookingNo || row?.originalBooking?.primaryBookingNo || row?.originalBooking?.bookingNo;
    if (!bookingNo || row.bookingStatus !== "confirmed") return;
    try {
      setDownloadingTicketId(bookingNo);
      const response = await downloadTicketByBookingNo(bookingNo);
      if (response) {
        const blob = new Blob([response], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
      } else {
        toast.error("Failed to download ticket PDF.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to download ticket PDF.");
    } finally {
      setDownloadingTicketId(null);
    }
  };

  const columnMap = useMemo<Record<string, ColumnDef<any>>>(() => ({
    bookingNo: { accessorKey: "bookingNo", header: "BOOKING NO", cell: ({ row }) => <span className="font-mono contact-name-text fw-bold fs-12px">{row.original.bookingNo}</span> },
    name: { accessorKey: "name", header: "NAME", cell: ({ row }) => <span className="fw-bold contact-name-text fs-13px">{row.original.name}</span> },
    email: { accessorKey: "email", header: "EMAIL", cell: ({ row }) => <span className="font-mono contact-sub-text fs-12px">{row.original.email}</span> },
    phone: { accessorKey: "phone", header: "PHONE", cell: ({ row }) => <span className="font-mono contact-sub-text fs-12px">{row.original.phone}</span> },
    ticketType: { accessorKey: "ticketType", header: "TICKET TYPE", cell: ({ row }) => <span className="v3-soft-pill">{row.original.ticketType}</span> },
    ticketCount: { accessorKey: "ticketCount", header: "TICKET COUNT", cell: ({ row }) => <span className="font-mono contact-name-text fw-semibold fs-12px">{row.original.ticketCount}</span> },
    ticketAmount: { accessorKey: "ticketAmount", header: "TICKET AMOUNT", cell: ({ row }) => <span className="font-mono contact-name-text fw-semibold fs-12px">{currencySymbol}{row.original.ticketAmount.toFixed(2)}</span> },
    grandTotal: { accessorKey: "grandTotal", header: "GRAND TOTAL", cell: ({ row }) => <span className="font-mono text-pink fw-bold fs-13px">{currencySymbol}{row.original.grandTotal.toFixed(2)}</span> },
    bookingStatus: {
      accessorKey: "bookingStatus",
      header: "BOOKING STATUS",
      cell: ({ row }) => {
        const status = String(row.original.bookingStatus).toLowerCase();
        const isConfirmed = status === "confirmed";
        return <span className={`v3-status-pill ${isConfirmed ? "is-confirmed" : "is-muted"}`}><ShieldCheck size={12} />{isConfirmed ? "Confirmed" : status.toUpperCase()}</span>;
      },
    },
    eventName: { accessorKey: "eventName", header: "EVENT NAME", cell: ({ row }) => <span className="contact-name-text fs-12px fw-semibold">{row.original.eventName}</span> },
    venueName: { accessorKey: "venueName", header: "VENUE NAME", cell: ({ row }) => <span className="contact-sub-text fs-12px">{row.original.venueName}</span> },
    couponCode: { accessorKey: "couponCode", header: "COUPON CODE", cell: ({ row }) => <span className="font-mono contact-sub-text fs-12px">{row.original.couponCode}</span> },
    discountAmount: { accessorKey: "discountAmount", header: "DISCOUNT", cell: ({ row }) => <span className="font-mono contact-sub-text fs-12px">{currencySymbol}{row.original.discountAmount.toFixed(2)}</span> },
    createdAt: { accessorKey: "createdAt", header: "CREATED DATE", cell: ({ row }) => <span className="contact-sub-text fs-11px font-mono">{row.original.createdAt ? <FormattedDate dateString={row.original.createdAt} /> : "N/A"}</span> },
    action: {
      id: "action",
      header: "ACTION",
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => {
        const isDownloading = downloadingTicketId === row.original.bookingNo;
        const canDownload = row.original.bookingStatus === "confirmed";
        return (
          <button type="button" className="v3-btn-primary" disabled={!canDownload || isDownloading} onClick={() => handleDownloadTicket(row.original)} style={{ padding: "6px 12px", fontSize: 11 }}>
            {isDownloading ? <PulseLoader color="#ffffff" size={4} /> : <><Download size={13} /> Ticket</>}
          </button>
        );
      },
    },
  }), [currencySymbol, downloadingTicketId]);

  const columns = useMemo<ColumnDef<any>[]>(() => selectedColumns.map((key) => columnMap[key]).filter(Boolean), [columnMap, selectedColumns]);

  const exportToExcel = () => {
    if (!transformedData.length) return;
    try {
      const XLSX = require("xlsx");
      const FileSaver = require("file-saver");
      const cleanData = transformedData.map((row) => {
        const exportRow: Record<string, any> = {};
        availableColumns.filter((col) => selectedColumns.includes(col.value) && col.value !== "action").forEach((col) => {
          const value = row[col.value as keyof typeof row];
          exportRow[col.label] = col.value === "createdAt" && value ? moment(value as string).format("YYYY-MM-DD HH:mm") : value;
        });
        return exportRow;
      });
      const worksheet = XLSX.utils.json_to_sheet(cleanData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Event Run Report");
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const file = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      FileSaver.saveAs(file, `event_run_report_${moment().format("YYYY-MM-DD_HH-mm")}.xlsx`);
      toast.success("Report spreadsheet exported!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to export Excel report.");
    }
  };

  const selectStyles = {
    control: (provided: any, state: any) => ({ ...provided, backgroundColor: isNightMode ? "rgba(255, 255, 255, 0.05)" : "#ffffff", borderColor: state.isFocused ? "#EE3F7C" : isNightMode ? "rgba(255, 255, 255, 0.08)" : "#cbd5e1", borderRadius: 12, minHeight: 44, boxShadow: "none", fontSize: 12 }),
    // menuPortal: menu is rendered on document.body so summary cards cannot cover it
    menuPortal: (provided: any) => ({ ...provided, zIndex: 99999 }),
    menu: (provided: any) => ({ ...provided, backgroundColor: isNightMode ? "rgba(18, 18, 28, 0.98)" : "#ffffff", border: isNightMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #e2e8f0", borderRadius: 14, zIndex: 99999, padding: 6 }),
    option: (provided: any, state: any) => ({ ...provided, backgroundColor: state.isSelected ? "rgba(238, 63, 124, 0.2)" : state.isFocused ? (isNightMode ? "rgba(255, 255, 255, 0.08)" : "#f1f5f9") : "transparent", color: isNightMode ? "#ffffff" : "#0f172a", cursor: "pointer", fontSize: 12 }),
    singleValue: (provided: any) => ({ ...provided, color: isNightMode ? "#ffffff" : "#0f172a" }),
    multiValue: (provided: any) => ({ ...provided, backgroundColor: "rgba(238, 63, 124, 0.14)", borderRadius: 8 }),
    multiValueLabel: (provided: any) => ({ ...provided, color: isNightMode ? "#ffffff" : "#0f172a", fontSize: 11 }),
    input: (provided: any) => ({ ...provided, color: isNightMode ? "#ffffff" : "#0f172a" }),
    placeholder: (provided: any) => ({ ...provided, color: isNightMode ? "rgba(255, 255, 255, 0.35)" : "#94a3b8", fontSize: 12 }),
  };

  const filteredSearchTypeOptions = searchTypeOptions.filter((option) => option.value !== "venue" || hasMultipleVenues);

  return (
    <div className={`contact-list-container ${isNightMode ? "text-white" : "text-dark"}`}>
      <Background />
      <ToastContainer position="top-right" autoClose={3000} theme={isNightMode ? "dark" : "light"} />

      <PageHeader
        title="Event Run Report"
        subtitle="Search this event's bookings by date, ticket, venue, or contact details, then filter and export the result table."
        icon={BarChart3}
        isLoading={loading}
        actions={
          <button
            type="button"
            className="v3-btn-primary"
            disabled={transformedData.length === 0}
            onClick={exportToExcel}
            style={{
              fontSize: 12,
              opacity: transformedData.length === 0 ? 0.6 : 1,
              cursor: transformedData.length === 0 ? "not-allowed" : "pointer"
            }}
          >
            <FileSpreadsheet size={15} /> Export Report (.XLSX)
          </button>
        }
      />

      <div className="glass-panel mb-4" style={{ display: "flex", flexDirection: "column", gap: 16, position: "relative", zIndex: 30 }}>
        <div className="v3-section-head"><Filter size={14} color="#EE3F7C" /> Search Criteria & Filters</div>
        <div>
          <label className="v3-label">Search Criteria Mode</label>
          <div className="d-flex flex-wrap gap-2">
            {filteredSearchTypeOptions.map((option) => {
              const Icon = option.icon;
              const isActive = searchCriteria.searchType === option.value;
              return <button key={option.value} type="button" className={isActive ? "v3-btn-primary" : "v3-btn-secondary"} onClick={() => handleSearchTypeChange(option.value)} style={{ padding: "8px 14px", fontSize: 12 }}><Icon size={14} /> {option.label}</button>;
            })}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
          {searchCriteria.searchType === "date" && <>
            <div><label className="v3-label">From Date</label><FlatPickr className="v3-input" placeholder="Select start date" value={searchCriteria.fromDate || ""} onChange={(dates: any) => setSearchCriteria((prev) => ({ ...prev, fromDate: dates[0] || null }))} /></div>
            <div><label className="v3-label">To Date</label><FlatPickr className="v3-input" placeholder="Select end date" value={searchCriteria.toDate || ""} onChange={(dates: any) => setSearchCriteria((prev) => ({ ...prev, toDate: dates[0] || null }))} /></div>
          </>}
          {searchCriteria.searchType === "ticket" && (
            <div>
              <label className="v3-label">Ticket Type</label>
              <Select
                value={searchCriteria.selectedTicketType}
                onChange={(option: any) => setSearchCriteria((prev) => ({ ...prev, selectedTicketType: option }))}
                options={ticketTypeList}
                placeholder="Choose ticket type..."
                isClearable
                styles={selectStyles}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
              />
            </div>
          )}
          {searchCriteria.searchType === "venue" && (
            <div>
              <label className="v3-label">Venue</label>
              <Select
                value={searchCriteria.selectedVenue}
                onChange={(option: any) => setSearchCriteria((prev) => ({ ...prev, selectedVenue: option }))}
                options={venueList}
                placeholder="Choose venue..."
                isClearable
                styles={selectStyles}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
              />
            </div>
          )}
          {searchCriteria.searchType === "email" && <div><label className="v3-label">Email Address or Phone Number</label><input type="text" className="v3-input" value={searchCriteria.emailOrPhone} onChange={(event) => setSearchCriteria((prev) => ({ ...prev, emailOrPhone: event.target.value }))} placeholder="Enter email or phone..." /></div>}
        </div>

        <div className="d-flex gap-2 flex-wrap">
          <button type="button" className="v3-btn-primary" onClick={performBookingSearch} disabled={loading || !isSearchValid()} style={{ padding: "10px 22px", fontSize: 12 }}>{loading ? <PulseLoader color="#fff" size={6} /> : <><Search size={14} /> Search Reports</>}</button>
          <button type="button" className="v3-btn-secondary" onClick={handleClear} style={{ padding: "10px 18px", fontSize: 12 }}><X size={14} /> Clear</button>
        </div>
      </div>

      {transformedData.length > 0 && <div className="contact-stats-grid mb-4" style={{ position: "relative", zIndex: 1 }}>
        <div className="contact-stat-card"><div><div className="contact-stat-label">TOTAL BOOKINGS</div><div className="contact-stat-value">{summaryStats?.totalBookings || 0}</div></div><div className="contact-stat-icon pink"><Calendar size={22} /></div></div>
        <div className="contact-stat-card"><div><div className="contact-stat-label">TOTAL REVENUE</div><div className="contact-stat-value">{currencySymbol}{(summaryStats?.organizerRevenue || summaryStats?.totalRevenue || 0).toFixed(2)}</div></div><div className="contact-stat-icon green"><CheckCircle2 size={22} /></div></div>
        <div className="contact-stat-card"><div><div className="contact-stat-label">ATTENDEES</div><div className="contact-stat-value">{summaryStats?.totalTickets || 0}</div></div><div className="contact-stat-icon orange"><Ticket size={22} /></div></div>
        <div className="contact-stat-card"><div><div className="contact-stat-label">TOTAL DISCOUNTS</div><div className="contact-stat-value">{currencySymbol}{(summaryStats?.totalDiscount || 0).toFixed(2)}</div></div><div className="contact-stat-icon purple"><Percent size={22} /></div></div>
      </div>}

      {error && <div style={{ padding: "12px 16px", borderRadius: 12, background: "rgba(255,93,115,0.1)", border: "1px solid rgba(255,93,115,0.25)", color: "#FF5D73", fontSize: 12, display: "flex", alignItems: "center", gap: 8, marginBottom: 16, position: "relative", zIndex: 1 }}><AlertCircle size={16} /> {error}</div>}

      {transformedData.length > 0 && <div className="contact-filter-card mb-3" style={{ position: "relative", zIndex: 20 }}>
        <div className="d-flex flex-column gap-3">
          <div className="position-relative w-100"><Search className="position-absolute start-0 top-50 translate-middle-y ms-3 contact-sub-text" size={18} /><input type="text" className="contact-search-input" placeholder="Quick global filter across all fields..." value={tableSearchQuery} onChange={(event) => setTableSearchQuery(event.target.value)} /></div>
          <div>
            <label className="v3-label d-flex align-items-center gap-2"><Columns3 size={14} /> Columns</label>
            <Select
              isMulti
              value={availableColumns.filter((col) => selectedColumns.includes(col.value))}
              onChange={(options: any) => setSelectedColumns((options || []).map((option: any) => option.value))}
              options={availableColumns}
              placeholder="Select columns to display..."
              styles={selectStyles}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
            />
          </div>
        </div>
      </div>}

      <div style={{ position: "relative", zIndex: 1 }}>
        <TablesV2
          data={transformedData}
          columns={columns}
          isLoading={loading}
          searchQuery={tableSearchQuery}
          enableColumnFilters={transformedData.length > 0}
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
          getRowId={(row) => row.id}
          loadingMessage="Querying Event Booking Registry..."
          emptyMessage={bookingList.length === 0 ? "Search this event to generate a run report." : "No booking records match your filters."}
        />
      </div>
    </div>
  );
};

export default EventRunReport;